import { useInitialHqColorMode } from '~/utils/use-color-mode';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@venncity/block';
import { antdLocales, Button, generateAntTheme, Notification, VennDSProvider, type AntdLocaleKey } from '@venncity/venn-ds';
import { isFunction } from 'lodash-es';
import NProgress from 'nprogress';
import nprogress from 'nprogress/nprogress.css?url';
import { Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useHref,
  useMatches,
  useNavigate,
  useNavigation,
  useRouteLoaderData,
} from 'react-router';
import { useChangeLanguage } from 'remix-i18next/react';
import Smartlook from 'smartlook-client';
import { z } from 'zod';
import antdOverrides from '~/styles/ant-overrides.css?url';
import antd from '../public/antd.css?url';
import interVariable from '@venncity/venn-ds/src/fonts/InterVariable.woff2?url';
import fonts from '@venncity/venn-ds/src/styles/fonts.css?url';
import { badRequest } from '~/utils/http.server';
import { GlobalSearchParam } from '~/utils/search-params';
import type { Route } from './+types/root';
import { ErrorPage } from './components/error-page';
import { UserPermissionProvider } from './components/user-context';
import { loadAnalytics, useBiEvents } from './lib/bi-events';
import { requireLogin } from './lib/auth.server';
import { queryClient } from './lib/cache.client';
import { createQueryClient } from './lib/query-client';
import { i18next } from './lib/i18next.server';
import { getMatchedRouteNamespaces } from './lib/route-namespaces';
import { getSession, sessionHeaders, sessionStorage } from './lib/session.server';
import { createVennGraphClient } from './lib/venn-graph-client.server';
import app from './styles/app.css?url';
import { resolveApplicantAppUrl } from './utils/applicant-app-url';
import { NotificationProvider } from './utils/context/NotificationContext';
import { PageInteractionLockProvider } from './utils/context/PageInteractionLockContext';

type NotificationProps = {
  message: ReactNode;
  description?: ReactNode;
  type?: 'success' | 'error';
  action?: ReactNode;
  timestamp?: number;
  duration?: number;
};

export const links: Route.LinksFunction = () => [
  {
    rel: 'preload',
    href: interVariable,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
  { rel: 'stylesheet', href: fonts },
  { rel: 'stylesheet', href: app },
  { rel: 'stylesheet', href: nprogress },
  { rel: 'stylesheet', href: antd },
  { rel: 'stylesheet', href: antdOverrides },
  // .ico first as the broad fallback; browsers that support SVG icons prefer the svg,
  // which recolours the mark to white under prefers-color-scheme: dark.
  { rel: 'icon', href: '/favicon.ico', sizes: '32x32' },
  { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
];

export const meta: Route.MetaFunction = () => [{ title: 'Venn Experience Hub' }];

export async function loader({ request }: Route.LoaderArgs) {
  const session = await getSession(request);
  const message = session.get('message');

  const locale = await i18next.getLocale(request);

  const isProd = process.env.NODE_ENV === 'production';

  const headers = new Headers();

  headers.append('Set-Cookie', await sessionStorage.commitSession(session));

  // Deliberately NOT writing the `lng` cookie here. The app layout writes it when a member's stored
  // preference disagrees with what was detected, and root runs on the same request — two loaders
  // emitting `Set-Cookie: lng` for one response makes the winner depend on header merge order.
  // Re-serializing here bought nothing anyway: detection is deterministic from the same request, so
  // dropping it only means a logged-out visitor resolves from Accept-Language each time, as before.

  return data(
    {
      message,
      locale,
      ENV: {
        SEGMENT_WRITE_KEY: process.env.SEGMENT_WRITE_KEY,
        ENVIRONMENT: isProd ? process.env.ENVIRONMENT : 'dev',
        LOG_LEVEL: process.env.LOG_LEVEL,
        VENN_GRAPH_URL: process.env.VENN_GRAPH_URL,
        VENN_GRAPH_TENANT_URL: process.env.VENN_GRAPH_TENANT_URL,
        GRAPHQL_API: process.env.GRAPHQL_API,
        NODE_ENV: process.env.NODE_ENV,
        SMARTLOOK_API_KEY: process.env.SMARTLOOK_API_KEY,
        APPLICANT_APP_URL: resolveApplicantAppUrl({
          isProd,
          isOnebox: process.env.IS_ONEBOX === 'true',
          clusterName: process.env.CLUSTER_NAME,
          requestUrl: request.url,
        }),
      },
      SESSION: {
        LOGIN_METHOD: session.get('login-method'),
        ORGANIZATION_ID: session.get('organization-id'),
      },
    },
    { headers },
  );
}

export async function action({ request }: Route.ActionArgs) {
  await requireLogin(request);

  const formData = await request.formData();

  const hood = formData.get('hood');

  const AppContextSchema = z.object({
    hood: z.string().min(1),
  });

  try {
    const validated = await AppContextSchema.parseAsync({
      hood,
    });

    const client = await createVennGraphClient(request);

    const { community } = await client.query({
      community: {
        __args: {
          id: validated.hood,
        },
        timezone: true,
      },
    });

    const session = await getSession(request);

    session.set('app-context', {
      type: 'neighborhood',
      hood: validated.hood,
    });

    session.set(GlobalSearchParam.CommunityTimezone, community.timezone);

    return data({ success: true }, { headers: await sessionHeaders(session) });
  } catch (error) {
    console.error(error);
    return badRequest({ success: false, errors: {} });
  }
}

// SSR and hydration preload the matched routes' `handle.i18n` namespaces, but a client-side navigation
// does not, so the next page rendered before its /locales json arrived and showed raw keys. Suspending
// here holds the navigation (React Router commits it in a transition, so the current page stays up)
// until every namespace the new routes declare is loaded.
function RouteNamespacesGate({ children }: { children: ReactNode }) {
  const matches = useMatches();
  const namespaces = useMemo(() => getMatchedRouteNamespaces(matches), [matches]);
  useTranslation(namespaces, { useSuspense: true });
  return <>{children}</>;
}

function App({ loaderData }: Route.ComponentProps) {
  // A server render gets its own client: the Hub layout hydrates a member's data into it, which must not reach anyone
  // else.
  const [client] = useState(() => (typeof document === 'undefined' ? createQueryClient() : queryClient));
  useChangeLanguage(loaderData.locale);
  const navigation = useNavigation();

  const { pageName, handleOnCloseEvent, onFormSubmit } = useBiEvents();
  const navigate = useNavigate();
  const antTheme = generateAntTheme('venn');

  const [api, ToastNotification] = Notification.useNotification();

  const openNotification = useCallback(
    ({ message, description, type, action, timestamp, duration }: NotificationProps) => {
      api[type || 'success']({
        message: message,
        description: description,
        placement: 'bottomRight',
        btn: action ? action : undefined,
        role: 'status',
        key: `notification-${timestamp || Date.now()}`,
        props: {
          'aria-label': `${message}`,
          role: 'alert',
        },
        duration: duration,
      });
    },
    [api],
  );

  useEffect(() => {
    if (!isFunction(NProgress.done) || !isFunction(NProgress.start)) return;

    if (navigation.state === 'loading' && !navigation.formMethod) {
      NProgress.start();
    } else {
      NProgress.done();
    }

    return () => {
      NProgress.done();
    };
  }, [navigation.formMethod, navigation.state]);

  useEffect(() => {
    if (loaderData.ENV.SMARTLOOK_API_KEY) {
      Smartlook.init(loaderData.ENV.SMARTLOOK_API_KEY);
    }
  }, [loaderData.ENV.SMARTLOOK_API_KEY]);

  useEffect(() => {
    if (loaderData.ENV.SEGMENT_WRITE_KEY) {
      loadAnalytics(loaderData.ENV.SEGMENT_WRITE_KEY);
    }
  }, [loaderData.ENV.SEGMENT_WRITE_KEY]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleOnCloseEvent);
    return () => {
      window.removeEventListener('beforeunload', handleOnCloseEvent);
    };
  }, [handleOnCloseEvent]);

  useEffect(() => {
    if (loaderData.message) {
      const toastNotificationContent: NotificationProps = {
        message: loaderData.message.title,
        description: loaderData.message.description,
        type: loaderData.message.variant,
        timestamp: loaderData.message._timestamp,
        duration: loaderData.message.duration,
      };

      if (loaderData.message.link) {
        toastNotificationContent.action = (
          <Button type="link" href={loaderData.message.link.href}>
            {loaderData.message.link.text}
          </Button>
        );
      }
      openNotification(toastNotificationContent);
    }
  }, [
    loaderData.message?.title,
    loaderData.message?.description,
    loaderData.message?.variant,
    loaderData.message?.link?.href,
    loaderData.message?._timestamp,
    openNotification,
  ]);

  return (
    <>
      <div className="flex h-full w-full flex-1 flex-col" onSubmit={(e) => onFormSubmit(e, pageName)}>
        <RouterProvider navigate={navigate} useHref={useHref}>
          <QueryClientProvider client={client}>
            <VennDSProvider locale={antdLocales[loaderData.locale as AntdLocaleKey] ?? antdLocales.en} theme={antTheme}>
              <NotificationProvider notify={(message, type = 'error') => openNotification({ message, type })}>
                <PageInteractionLockProvider>
                  <UserPermissionProvider>
                    <Suspense fallback={null}>
                      <RouteNamespacesGate>
                        <Outlet />
                      </RouteNamespacesGate>
                    </Suspense>
                    {ToastNotification}
                  </UserPermissionProvider>
                </PageInteractionLockProvider>
              </NotificationProvider>
            </VennDSProvider>
          </QueryClientProvider>
        </RouterProvider>
      </div>
    </>
  );
}

export default App;

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // For other errors, determine message and status
  console.error('Error in ErrorBoundary', JSON.stringify(error));
  let status = undefined;
  let message = undefined;

  if (isRouteErrorResponse(error)) {
    status = error.status;
  } else if (error && error instanceof Error) {
    Sentry.captureException(error);
    if (import.meta.env.DEV) {
      message = error.message;
    }
  }

  if (
    message &&
    (message.toLowerCase().includes('access denied') ||
      message.toLowerCase().includes('permission denied') ||
      message.toLowerCase().includes('forbidden'))
  ) {
    status = 403;
  }

  // For non-permission errors, show the error page
  return <ErrorPage message={message} status={status} />;
}

export function Layout({ children }: { children: ReactNode }) {
  const loaderData = useRouteLoaderData<typeof loader>('root');
  const { i18n } = useTranslation();
  const hqColorMode = useInitialHqColorMode();

  return (
    <html data-hq-theme={hqColorMode} className="h-full" dir={i18n.dir()} lang={loaderData?.locale ?? 'en'}>
      <head>
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <Meta />
        <Links />
        {/* FullCalendar injects styles at runtime. This placeholder is required for SSR frameworks (per FullCalendar Remix guidance). might be changed in V7 that will support  tailwind */}
        <style data-fullcalendar />
      </head>
      <body className="h-full w-full antialiased">
        {children}
        {loaderData?.ENV && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window.ENV = ${JSON.stringify(loaderData?.ENV)}
              `,
            }}
          />
        )}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const handle = {
  i18n: 'translation',
};
